import { useEffect, useState } from 'react';
import {
  Heading,
  Text,
  Box,
  Center,
  Card,
  CardHeader,
  CardBody,
  Button,
  SimpleGrid,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Helmet } from 'react-helmet';

const WAITLIST_API_URL =
  'https://sckumwyv6l.execute-api.eu-central-1.amazonaws.com/shopping-list-waitlist';

// Replace each stripeUrl with the matching Stripe Payment Link for that product.
const VARIANTS = [
  {
    id: 'Pirkinių sąrašas - S',
    title: 'Drabužiai',
    size: 'S',
    stripeUrl: 'https://buy.stripe.com/14A00k2Oi7Lx9SGb6qeQM00',
  },
  {
    id: 'Pirkinių sąrašas - M',
    title: 'Drabužiai',
    size: 'M',
    stripeUrl: 'https://buy.stripe.com/dRm8wQ9cGaXJ4ymgqKeQM01',
  },
  {
    id: 'Pirkinių sąrašas - L, XL, XXL, XXXL',
    title: 'Drabužiai',
    size: 'L, XL, XXL, XXXL',
    stripeUrl: 'https://buy.stripe.com/3cI6oI0Ga2rd3uia2meQM02',
  },
  {
    id: 'Pirkinių sąrašas - 37',
    title: 'Batai',
    size: '37',
    stripeUrl: 'https://buy.stripe.com/cNi5kEcoS0j53ui7UeeQM03',
  },
  {
    id: 'Pirkinių sąrašas - 38',
    title: 'Batai',
    size: '38',
    stripeUrl: 'https://buy.stripe.com/8x2aEY0Gae9Vd4Sb6qeQM04',
  },
  {
    id: 'Pirkinių sąrašas - 39',
    title: 'Batai',
    size: '39',
    stripeUrl: 'https://buy.stripe.com/3cI4gA2Oi3vhc0O2zUeQM05',
  },
  {
    id: 'Pirkinių sąrašas - 40',
    title: 'Batai',
    size: '40',
    stripeUrl: 'https://buy.stripe.com/4gM3cwdsWgi34ymfmGeQM06',
  },
  {
    id: 'Pirkinių sąrašas - 41, 42',
    title: 'Batai',
    size: '41, 42',
    stripeUrl: 'https://buy.stripe.com/9B6cN63Smd5RaWK3DYeQM07',
  },
];

const buttonBackground = {
  backgroundImage:
    "linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)), url('/shopping-list-background.jpg')",
  backgroundSize: 'cover',
  backgroundPosition: 'center 30%',
  backgroundRepeat: 'no-repeat',
  backgroundColor: 'transparent',
};

const variantButtonSx = {
  w: '100%',
  h: '100%',
  minH: { base: '220px', md: '260px' },
  borderWidth: '2px',
  borderColor: 'black',
  borderRadius: '8px',
  ...buttonBackground,
  color: 'white',
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 'bold',
  whiteSpace: 'normal',
  px: 3,
  py: 6,
  _hover: {
    ...buttonBackground,
    backgroundImage:
      "linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)), url('/shopping-list-background.jpg')",
  },
  _active: {
    ...buttonBackground,
  },
  _focus: {
    ...buttonBackground,
  },
  _focusVisible: {
    ...buttonBackground,
  },
  _disabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
};

const outlineButtonSx = {
  color: 'black.500',
  borderColor: 'black.500',
  borderWidth: '2px',
  '&:hover': {
    bg: 'rgba(251, 143, 255, 0.4)',
  },
};

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function ShoppingLists() {
  const [inventoryByName, setInventoryByName] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    fetch('https://sckumwyv6l.execute-api.eu-central-1.amazonaws.com/shopping-list-inventory', {
      method: 'GET',
    })
      .then((res) => res.json())
      .then((data) => {
        const byName = {};
        if (Array.isArray(data)) {
          data.forEach((item) => {
            const name = item['shopping-list-name'];
            if (name) {
              byName[name] = item;
            }
          });
        }
        setInventoryByName(byName);
      })
      .catch((error) => {
        console.error('Error fetching shopping list inventory:', error);
      });
  }, []);

  const closeWaitlistModal = () => {
    onClose();
    setSelectedVariant(null);
    setEmail('');
    setEmailError('');
    setIsSubmitting(false);
  };

  const openWaitlistModal = (variant) => {
    setSelectedVariant(variant);
    setEmail('');
    setEmailError('');
    onOpen();
  };

  const handleWaitlistSubmit = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError('Įveskite el. paštą');
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setEmailError('Neteisingas el. pašto formatas');
      return;
    }
    if (!selectedVariant) {
      return;
    }

    setIsSubmitting(true);
    setEmailError('');

    try {
      const res = await fetch(WAITLIST_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          productName: selectedVariant.id,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      toast({
        title: 'Puiku!',
        description: 'Informuosiu, kai parengsiu naują shopping list\'ą.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      closeWaitlistModal();
    } catch (error) {
      console.error('Error joining waitlist:', error);
      toast({
        title: 'Klaida',
        description: 'Nepavyko užsiregistruoti į laukiančiųjų sąrašą. Bandykite dar kartą.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Ką rengtis pagal Viliją Rušinskaitę - Shopping list'ai</title>
        <meta name="description" content="Vilijos Rušinskaitės shopping list'ai" />
        <meta property="og:title" content="Shopping list'ai" />
        <meta property="og:description" content="Vilijos Rušinskaitės shopping list'ai" />
        <meta property="og:image" content="https://karengtis.lt/vilija1_small.jpg" />
        <meta property="og:url" content="https://www.karengtis.lt/shopping-listai" />
      </Helmet>

      <Box minH="2vw" />
      <Center px={{ base: 4, md: 0 }}>
        <Card
          border="1px"
          maxWidth="80vw"
          minWidth={{ base: 'auto', md: '60vw' }}
          w="100%"
          shadow="none"
        >
          <CardHeader pb={2}>
            <Box minH="1vw" />
            <Center>
              <Heading
                as="h1"
                sx={{ fontSize: ['24px', '32px'], fontFamily: 'Montserrat, sans-serif' }}
              >
                Shopping list'ai
              </Heading>
            </Center>
            <Center mt={3}>
              <Text fontSize="lg" textAlign="center" maxW="36rem" color="gray.700">
                Pasirinkite dydį — būsite nukreipti į apmokėjimą.
              </Text>
            </Center>
          </CardHeader>

          <CardBody pt={4} pb={8} px={{ base: 4, md: 8 }}>
            <SimpleGrid columns={2} spacing={{ base: 3, md: 5 }}>
              {VARIANTS.map((variant) => {
                const status = inventoryByName[variant.id]?.status;
                const isAvailable = status === 'AVAILABLE';
                const isSoldOut = status === 'SOLD OUT';
                const isClickable = isAvailable || isSoldOut;

                return (
                  <Button
                    key={variant.id}
                    as={isAvailable ? 'a' : 'button'}
                    href={isAvailable ? variant.stripeUrl : undefined}
                    target={isAvailable ? '_blank' : undefined}
                    rel={isAvailable ? 'noopener noreferrer' : undefined}
                    type={isAvailable ? undefined : 'button'}
                    isDisabled={!isClickable}
                    variant="outline"
                    aria-label={
                      isSoldOut
                        ? `Prisijungti prie laukiančiųjų sąrašo: ${variant.title} - ${variant.size}`
                        : `Pirkti ${variant.title} - ${variant.size}`
                    }
                    onClick={
                      isSoldOut
                        ? (event) => {
                            event.preventDefault();
                            openWaitlistModal(variant);
                          }
                        : undefined
                    }
                    sx={{
                      ...variantButtonSx,
                      borderColor: isSoldOut ? 'red.500' : 'black',
                      borderWidth: isSoldOut ? '3px' : '2px',
                    }}
                  >
                    <VStack spacing={1} w="100%" textAlign="center">
                      <Text
                        fontSize={{ base: 'md', md: '2xl' }}
                        fontWeight="bold"
                        lineHeight="1.2"
                      >
                        {variant.title}
                      </Text>
                      <Text
                        fontSize={{ base: 'md', md: '2xl' }}
                        fontWeight="bold"
                        lineHeight="1.2"
                      >
                        {variant.size}
                      </Text>
                    </VStack>
                  </Button>
                );
              })}
            </SimpleGrid>
          </CardBody>
        </Card>
      </Center>
      <Box minH="2vw" />

      <Modal isOpen={isOpen} onClose={closeWaitlistModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Prisijungti prie waitlist'o</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <Text mb={4} fontSize="md">
              {selectedVariant
                ? `Shopping list'as buvo išparduotas 💔 Bet apie būsimus shopping list'us galite sužinoti pirmieji palikę savo el. paštą.`
                : 'Palikite el. paštą ir pranešime, kai prekė vėl bus prieinama.'}
            </Text>
            <FormControl isInvalid={Boolean(emailError)}>
              <FormLabel>El. paštas</FormLabel>
              <Input
                type="email"
                value={email}
                placeholder="jusu@email.com"
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (emailError) {
                    setEmailError('');
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleWaitlistSubmit();
                  }
                }}
              />
              <FormErrorMessage>{emailError}</FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="white"
              variant="outline"
              sx={outlineButtonSx}
              mr={3}
              onClick={handleWaitlistSubmit}
              isLoading={isSubmitting}
            >
              Pateikti
            </Button>
            <Button
              colorScheme="white"
              variant="outline"
              sx={{
                ...outlineButtonSx,
                bg: '#E9E9E9',
                '&:hover': { bg: '#E0E0E0' },
              }}
              onClick={closeWaitlistModal}
              isDisabled={isSubmitting}
            >
              Atšaukti
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
